// Product brief for Global Atlantic ForeCare and the generic annuityHybrid
// fallback. See cmTwo.ts for the format intent.

export const forecareBrief = `**Global Atlantic ForeCare** — single-premium fixed annuity with accelerated
LTC benefits (calculationType \`annuityHybrid\`). Also models the generic
\`annuityHybrid\` type as the closest single-pool fallback when the ForeCare
guaranteed field set is incomplete.

Guaranteed ForeCare model — **Contract Value first, then Extended Benefit**:
- When \`policyBaseBenefitAmountAtIssue\`, \`policyContinuationBenefitAmountAtIssue\`,
  guaranteed Contract Value rates, and \`policyMonthlyContinuationBenefitChargeRate\`
  are present, the strategy uses the guaranteed-only carrier ledger.
- \`base_pool\` carries the remaining **Accelerated Benefit / Contract Value**.
  \`eob\` carries the remaining **Maximum Extended Benefit**. LTC draws deplete
  Contract Value first, then Extended Benefit.
- \`db\` mirrors the remaining Contract Value residual. Accelerated LTC
  withdrawals reduce it, guaranteed interest can increase it, and any value left
  after the claim is the residual non-LTC payout.

Fallback model — **legacy single pool + Contract Value floor**:
- If the guaranteed field set is incomplete, \`policyMaximumBenefitAmount\` still
  seeds one Total LTC Benefit pool and \`eob\` remains $0. The Contract Value
  floor, when populated, is carried in \`db\` and reduced by LTC draws.

Daily-cap + pool inflation:
- ForeCare's sample illustration does not elect the LTC inflation rider, so the
  guaranteed model keeps the Maximum Monthly Benefit and Maximum Extended
  Benefit flat. The Contract Value schedule is derived from guaranteed interest
  rates and monthly rider charges, with charges waived in LTC withdrawal months.
- The fallback path still honors a non-zero \`policyInflationProtection\` value
  for legacy/generic annuity-hybrid rows.

Payout model — reimbursement (default) or cash indemnity:
- **Reimbursement** (default): pays min(eligible cost, pro-rated daily cap) from
  the pool. Care cost above the cap is out-of-pocket.
- **Indemnity** (election): pays min(pro-rated cap, pool remaining) as cash.

Elimination period:
- Elimination-period days are out-of-pocket (\`in-wp\` rows). No retroactive lump.

Ground truth — sample illustration (\`55F ForeCare.pdf\`, Female 55, CA,
$100,000 single premium, "Single Life - Premier (3X contract value)", no LTC
inflation rider):
- Year-1 **Total LTC Benefit $303,501** = Accelerated Benefit / Contract Value
  $103,501 + Maximum Extended Benefit $200,000 (pool seed).
- **Maximum Monthly Benefit $4,167** flat every year (≈ $138.90/day). The
  monthly cap binds, not the pool: $4,167 × 72mo = $300,024, just under the
  pool, with the gap covered by interest credited during drawdown (carrier's
  claim example pays $303,892 total).
- Scheduled benefit period 24mo accelerated + 48mo extended = **72 months**.
- Contract Value floor **$103,501** (year-1 value). Short-claim residual check:
  ~18mo × $4,167 ≈ $75k drawn → residual ≈ $28k. Long claim: carrier example
  drains the Contract Value to $0 (residual $0) after ~$150k drawn.

Things to eyeball:
- Guaranteed rows should show \`base_pool\` drawing before \`eob\`; fallback rows
  keep \`eob\` at $0.
- Residual: when total LTC drawn is below the Contract Value floor, a residual
- non-LTC payout surfaces; once Contract Value is exhausted the residual is $0
  and Extended Benefit draws do not reduce \`db\`.
- Out-of-pocket appears whenever care cost exceeds the inflated monthly cap.
`;
